import {E, GetEntries} from "js-vextensions";
import {GetAsync, observer_mgl} from "mobx-graphlink";
import React from "react";
import {Button, Column, Row, Select, Text} from "react-vcomponents";
import {ScrollView} from "react-vscrollview";
import {ES, PageContainer, RunInAction_Set} from "web-vcore";
import {store} from "../../Store/index.js";
import {EchoHuntTab} from "../../Store/main/tools/echoHunt";// dev-only imports (used by the temporary storage round-trip check in the Settings panel)
import {EchoCorpusBullet, GetEchoCorpusBullet} from "../../Server/DB/echoCorpusBullets/@EchoCorpusBullet.js";
import {EchoProfile, GetEchoProfile} from "../../Server/DB/echoProfiles/@EchoProfile.js";
import {EchoRound, GetEchoRound} from "../../Server/DB/echoRounds/@EchoRound.js";
import {EchoStory, GetEchoStory} from "../../Server/DB/echoStories/@EchoStory.js";
import {AddEchoCorpusBullet, DeleteEchoCorpusBullet} from "../../Server/Commands/EchoCorpusBulletCommands.js";
import {AddEchoProfile, DeleteEchoProfile} from "../../Server/Commands/EchoProfileCommands.js";
import {AddEchoRound, DeleteEchoRound} from "../../Server/Commands/EchoRoundCommands.js";
import {AddEchoStory, DeleteEchoStory} from "../../Server/Commands/EchoStoryCommands.js";
import {Me} from "../../Server/DB/users.js";
import {EnsureWorldPool_Live} from "../../EchoHunt/Weave_Live.js";
import {ModelRolesPanel} from "./EchoHunt/ModelRolesPanel.js";
import {WeaverPanel} from "./EchoHunt/WeaverPanel.js";
import {CorpusPanel} from "./EchoHunt/CorpusPanel.js";
import {FakeGeneratorDevPanel, WordlistPanel} from "./EchoHunt/WordlistPanel.js";
import {BulletPoolDevPanel} from "./EchoHunt/ReshapePanel.js";
import {WeaveDevPanel} from "./EchoHunt/WeavePanel.js";
import {JudgePanel} from "./EchoHunt/JudgePanel.js";
import {JudgeInspectorPanel} from "./EchoHunt/JudgeInspectorPanel.js";
import {WorldsPanel} from "./EchoHunt/WorldsPanel.js";
import {CharactersPanel} from "./EchoHunt/CharactersPanel.js";
import {BlacklistPanel} from "./EchoHunt/BlacklistPanel.js";
import {PlayPanel} from "./EchoHunt/PlayPanel.js";
import {StoryPanel} from "./EchoHunt/StoryPanel.js";
import {SuspicionPanel} from "./EchoHunt/SuspicionPanel.js";

// ui
// ==========

export const EchoHuntUI = observer_mgl(()=>{
	const uiState = store.main.tools.echoHunt;

	// Seed the world pool + the default schematic rules once for a profile that predates P8. Done HERE
	// rather than in the Worlds tab, because the Play/weaver paths need a world to exist and the user may
	// never open that tab. Idempotent server-side (it writes only when the lists are empty).
	React.useEffect(()=>{
		EnsureWorldPool_Live().catch(()=>{ /* surfaced by the Worlds tab's readout */ });
	}, []);

	return (
		<PageContainer preset="text" style={E({padding: 0})}>
			<Row>
				<Select displayType="button bar" options={GetEntries(EchoHuntTab, "ui")} value={uiState.tab} onChange={val=>{
					RunInAction_Set(()=>uiState.tab = val);
				}}/>
			</Row>
			{uiState.tab == EchoHuntTab.play && <PlayPanel/>}
			{uiState.tab == EchoHuntTab.story && <StoryPanel/>}
			{uiState.tab == EchoHuntTab.corpus && <CorpusPanel/>}
			{uiState.tab == EchoHuntTab.worlds && <WorldsPanel/>}
			{uiState.tab == EchoHuntTab.characters && <CharactersPanel/>}
			{uiState.tab == EchoHuntTab.cliches && <BlacklistPanel/>}
			{uiState.tab == EchoHuntTab.settings && <SettingsPanel/>}
		</PageContainer>
	);
});

// panels
// ==========

// Settings tab -- per-role models, word list, and the dev tools that press a whole pipeline stage at once.
// The world pool and characters live in their OWN tabs (configured once), while everything here is what you
// touch repeatedly while tuning prompts.
const SettingsPanel = observer_mgl(()=>{
	return (
		<ScrollView style={ES({flex: 1, padding: 10})} contentStyle={{display: "flex", flexDirection: "column"}}>
			<Text>Echo Hunt -- Settings</Text>
			<Column mt={10} style={{gap: 10}}>
				<ModelRolesPanel/>
				{/* 2026-10-21: whether the weaver may reword freely, or only bridge (§8.1) */}
				<WeaverPanel/>
				{/* P6: the seed-word list the fake generator draws its concepts from, plus the manual-leakage
				    review tool (§9.5) */}
				<WordlistPanel/>
				<FakeGeneratorDevPanel/>
				{/* P7: the shared reshape + the round bullet pool, with labels hidden by default (§16.0) */}
				<BulletPoolDevPanel/>
				{/* P8: build a pool and weave it into a round (§8.1) */}
				<WeaveDevPanel/>
				{/* P10: the judge loop's band, judges, repair budget and held-out judge (§9) */}
				<JudgePanel/>
				{/* P11: the suspicion meter, including the opt-out from APPLYING it to the story (§8.4) */}
				<SuspicionPanel/>
				{/* P10 follow-up: what the loop tried on each bullet, and a forced per-bullet retry */}
				<JudgeInspectorPanel round={null}/>
			</Column>
			<DevStorageRoundTrip/>
		</ScrollView>
	);
});

// dev tool -- P2 acceptance criterion: create, read back and delete one row in each table.
// TEMPORARY: delete this component once P2 is signed off (see the P2 checklist in the plan-doc).
const DevStorageRoundTrip = observer_mgl(()=>{
	const [log, setLog] = React.useState<string[]>([]);
	const [busy, setBusy] = React.useState(false);
	return (
		<>
			<Row mt={10} style={{gap: 5}}>
				<Button text="Dev: storage round-trip (all 4 tables)" enabled={!busy} onClick={async()=>{
					setBusy(true);
					setLog([]);
					const logLine = (msg: string)=>setLog(prev=>[...prev, msg]);

					if (Me() == null) {
						logLine("Not signed in -- cannot run round-trip.");
						setBusy(false);
						return;
					}

					try {
						// helper: create -> read back -> delete
						// the read-back uses `GetAsync`, which resolves once the accessor's data has loaded -- so no
						// polling and no CatchBail is needed (CatchBail would treat "not loaded yet" as "not found").
						const roundTrip = async(name: string, create: ()=>Promise<string>, readBack: (id: string)=>Promise<unknown>, del: (id: string)=>Promise<unknown>)=>{
							const id = await create();
							logLine(`${name}: created ${id}`);
							const row = await readBack(id);
							logLine(`${name}: read back ${row != null ? "OK" : "FAILED (row was null)"}`);
							await del(id);
							logLine(`${name}: deleted`);
						};

						await roundTrip(
							"echoCorpusBullets",
							()=>new AddEchoCorpusBullet({entry: new EchoCorpusBullet({
								dreamId: "devEntry/devSegment",
								sourceTextHash: "devHash",
								rawText: "dev raw text",
							})}).RunOnServer().then(a=>a.id),
							id=>GetAsync(()=>GetEchoCorpusBullet(id)),
							id=>new DeleteEchoCorpusBullet({id}).RunOnServer(),
						);

						await roundTrip(
							"echoProfiles",
							()=>new AddEchoProfile({entry: new EchoProfile({xShare: 0.65, yTarget: 0.5})}).RunOnServer().then(a=>a.id),
							id=>GetAsync(()=>GetEchoProfile(id)),
							id=>new DeleteEchoProfile({id}).RunOnServer(),
						);

						await roundTrip(
							"echoStories",
							()=>new AddEchoStory({entry: new EchoStory({summary: "dev summary", suspicionAccumulated: 0})}).RunOnServer().then(a=>a.id),
							id=>GetAsync(()=>GetEchoStory(id)),
							id=>new DeleteEchoStory({id}).RunOnServer(),
						);

						await roundTrip(
							"echoRounds",
							()=>new AddEchoRound({entry: new EchoRound({status: "unjudged", xShare: 0.65, slots: []})}).RunOnServer().then(a=>a.id),
							id=>GetAsync(()=>GetEchoRound(id)),
							id=>new DeleteEchoRound({id}).RunOnServer(),
						);

						logLine("Done -- check for any FAILED lines above.");
					} catch (ex) {
						logLine(`ERROR: ${ex}`);
					}
					setBusy(false);
				}}/>
			</Row>
			{log.length > 0 && (
				<Column mt={10} style={{fontFamily: "monospace", fontSize: 12}}>
					{log.map((line, i)=><Text key={i}>{line}</Text>)}
				</Column>
			)}
		</>
	);
});
